<template>
  <section class="rounded-lg border border-slate-200 bg-white">
    <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
      <div>
        <h4 class="text-sm font-semibold text-slate-900">{{ title }}</h4>
        <p v-if="description" class="mt-0.5 text-xs text-slate-500">{{ description }}</p>
      </div>
      <div class="flex items-center gap-2">
        <el-select v-if="showModeSwitch" v-model="modeValue" class="form-select form-control-sm w-40">
          <el-option label="全部批次" value="all" />
          <el-option label="即将到期" value="upcoming" />
          <el-option label="已到期未处理" value="matured" />
        </el-select>
        <button type="button" class="btn-secondary" :disabled="loading" @click="loadBatches">刷新</button>
      </div>
    </div>
    <div class="data-table-shell border-0 shadow-none">
      <div class="data-table-scroll">
        <table class="data-table text-sm">
          <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left">批次编号</th>
              <th class="data-table-th text-right">剩余本金</th>
              <th class="data-table-th text-right">年利率（%）</th>
              <th class="data-table-th text-left">起息日</th>
              <th class="data-table-th text-left">到期日</th>
              <th class="data-table-th text-left">状态</th>
              <th class="data-table-th text-left">提醒</th>
              <th class="data-table-th text-left">进行中操作</th>
              <th v-if="$slots.actions" class="data-table-th text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="$slots.actions ? 9 : 8" class="data-table-empty">加载中...</td>
            </tr>
            <tr v-else-if="rows.length === 0">
              <td :colspan="$slots.actions ? 9 : 8" class="data-table-empty">暂无批次</td>
            </tr>
            <tr v-for="row in rows" v-else :key="row.id" class="data-table-row">
              <td class="data-table-td font-mono text-xs text-slate-900">{{ row.batchNo || '-' }}</td>
              <td class="data-table-td is-right font-medium text-slate-900">¥{{ formatMoney(row.remainingPrincipalAmount) }}</td>
              <td class="data-table-td is-right">{{ formatRate(row.annualRate) }}</td>
              <td class="data-table-td">{{ formatDate(row.valueDate) }}</td>
              <td class="data-table-td">{{ formatDate(row.maturityDate) }}</td>
              <td class="data-table-td"><WealthStatusBadge type="batch" :value="displayBatchStatus(row)" show-dot /></td>
              <td class="data-table-td">
                <span :class="['status-badge', row.reminderTaskId ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600']">
                  {{ row.reminderTaskId ? '已创建' : '未创建' }}
                </span>
              </td>
              <td class="data-table-td">
                <div v-if="ongoingOperations[row.id]?.length" class="space-y-1">
                  <div
                    v-for="operation in ongoingOperations[row.id]"
                    :key="`${operation.applyId}-${operation.batchId}`"
                    class="inline-flex items-center gap-1"
                  >
                    <button
                      type="button"
                      class="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200"
                      @click="$emit('view-operation', operation)"
                    >
                      {{ formatOngoingOperation(operation) }}
                    </button>
                    <button
                      v-if="operation.apply?.approvalInstanceId"
                      type="button"
                      class="table-action-button is-purple"
                      title="查看审批详情" aria-label="查看审批详情"
                      @click.stop="$emit('view-approval', operation)"
                    >
                      <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <span v-else class="text-xs text-slate-400">-</span>
              </td>
              <td v-if="$slots.actions" class="data-table-td is-center">
                <slot name="actions" :row="row" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <AppPagination
      v-if="pagination.total > 0"
      class="pagination-bridge px-4 pb-4"
      :pagination="pagination"
      :page-sizes="PAGE_SIZE_OPTIONS"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"
    />
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import AppPagination from '@/components/AppPagination.vue'
import {
  getWealthContractBatchPage,
  getWealthFundBatchPage,
  getWealthMaturedUnhandledBatchPage,
  getWealthUpcomingMaturityBatchPage
} from '@/api/wealth'
import WealthStatusBadge from './WealthStatusBadge.vue'
import { formatDate, formatMoney, formatRate, PAGE_SIZE_OPTIONS, pagePayload, syncPageResult } from '../utils'

const props = defineProps({
  title: {
    type: String,
    default: '资金批次'
  },
  description: {
    type: String,
    default: ''
  },
  contractId: {
    type: Number,
    default: null
  },
  mode: {
    type: String,
    default: 'all'
  },
  showModeSwitch: {
    type: Boolean,
    default: true
  },
  extraQuery: {
    type: Object,
    default: () => ({})
  },
  ongoingOperations: {
    type: Object,
    default: () => ({})
  }
})

defineEmits(['view-operation', 'view-approval'])

const rows = ref([])
const loading = ref(false)
const modeValue = ref(props.mode)
const pagination = reactive({
  current: 1,
  page: 1,
  size: 10,
  total: 0,
  pages: 1
})

const query = computed(() => pagePayload(pagination, props.extraQuery))

async function loadBatches() {
  loading.value = true
  try {
    let result
    if (props.contractId) {
      result = await getWealthContractBatchPage(props.contractId, query.value)
    } else if (modeValue.value === 'upcoming') {
      result = await getWealthUpcomingMaturityBatchPage(query.value)
    } else if (modeValue.value === 'matured') {
      result = await getWealthMaturedUnhandledBatchPage(query.value)
    } else {
      result = await getWealthFundBatchPage(query.value)
    }
    rows.value = result?.records || []
    syncPageResult(pagination, result)
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

function handlePageChange(page) {
  pagination.current = page
  pagination.page = page
  loadBatches()
}

function handleSizeChange(size) {
  pagination.size = size
  pagination.current = 1
  pagination.page = 1
  loadBatches()
}

function formatOngoingOperation(operation = {}) {
  const amount = operation.amount ? ` ¥${formatMoney(operation.amount)}` : ''
  return `${operation.operationTypeLabel || operation.operationType || '操作'}${operation.statusLabel ? ` ${operation.statusLabel}` : ''}${amount}`
}

function displayBatchStatus(row = {}) {
  if (row.status !== 'ACTIVE' || !row.maturityDate) {
    return row.status
  }
  const maturityDate = new Date(`${String(row.maturityDate).slice(0, 10)}T00:00:00`)
  if (Number.isNaN(maturityDate.getTime())) {
    return row.status
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const upcomingDeadline = new Date(today)
  upcomingDeadline.setMonth(upcomingDeadline.getMonth() + 1)
  if (maturityDate > today && maturityDate <= upcomingDeadline) {
    return 'UPCOMING_MATURITY'
  }
  return row.status
}

watch(() => props.mode, value => {
  modeValue.value = value
})

watch([modeValue, () => props.contractId, () => props.extraQuery], () => {
  pagination.current = 1
  pagination.page = 1
  loadBatches()
}, { immediate: true, deep: true })

defineExpose({ loadBatches })
</script>
